/**
 * Helpers for validating durable/SSE letter generation results before navigation.
 */

function unwrapLetterEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return entry.letter && typeof entry.letter === 'object' ? entry.letter : entry;
}

/** Remove debug preview banners from stored/generated HTML. */
export function stripDebugBanner(html) {
  if (!html || !String(html).trim()) return '';
  let result = String(html);
  result = result.replace(
    /<div[^>]*class=["'][^"']*preview-banner[^"']*["'][^>]*>[\s\S]*?<\/div>/gi,
    ''
  );
  result = result.replace(/PREVIEW\s*[–-]\s*Prompt Managed/gi, '');
  result = result.replace(/PREVIEW\s*[–-]\s*Few Shot/gi, '');
  result = result.replace(/PREVIEW\s*[–-]\s*Deterministic/gi, '');
  return result.trim();
}

/** Minimal iframe CSS: hide debug banners without overriding printable letter layout. */
export const PRINTABLE_PREVIEW_IFRAME_CSS = `
<style type="text/css">
.preview-banner, .preview-banner.no-print, .no-print.preview-banner { display: none !important; }
html, body { background: #fff !important; overflow-x: auto !important; margin: 0 !important; }
</style>
`;

/** Inject printable preview CSS into full-letter HTML for iframe display. */
export function injectPrintablePreviewCss(html) {
  if (!html || !String(html).trim()) return '';
  const cleaned = stripDebugBanner(html);
  if (cleaned.includes('</head>')) {
    return cleaned.replace('</head>', `${PRINTABLE_PREVIEW_IFRAME_CSS}</head>`);
  }
  if (/<body[^>]*>/i.test(cleaned)) {
    return cleaned.replace(/(<body[^>]*>)/i, `$1${PRINTABLE_PREVIEW_IFRAME_CSS}`);
  }
  return `${PRINTABLE_PREVIEW_IFRAME_CSS}${cleaned}`;
}

/**
 * Resolve HTML for Step 5 / LetterPreviewPage in-app preview.
 * Must match Download Printable Letter — never use postgrid_html.
 */
export function resolvePrintablePreviewHtml(data) {
  if (!data) return '';

  const postgridHtml = data.postgrid_html && String(data.postgrid_html).trim();

  const download = data.download_pdf_html && String(data.download_pdf_html).trim();
  if (download) return stripDebugBanner(download);

  const preview = data.preview_html && String(data.preview_html).trim();
  if (preview) return stripDebugBanner(preview);

  for (const candidate of [data.html, data.letter_html, data.content]) {
    if (!candidate || !String(candidate).trim()) continue;
    const value = String(candidate).trim();
    if (postgridHtml && value === postgridHtml) continue;
    return stripDebugBanner(value);
  }

  return stripDebugBanner(coerceLetterHtml(data));
}

/** Coerce any letter/package entry into renderable HTML when possible. */
export function coerceLetterHtml(data) {
  if (!data) return '';
  const direct =
    data.preview_html ||
    data.html ||
    data.letter_html ||
    data.content;
  if (direct && String(direct).trim()) {
    return String(direct);
  }

  const body = data.letter_body || data.letter_text || data.body;
  if (!body || !String(body).trim()) {
    return '';
  }

  const text = String(body);
  if (text.includes('<html') || text.includes('<body')) {
    return text;
  }

  const bureau = data.bureau || 'Credit Bureau';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Credit Dispute Letter - ${bureau}</title></head><body><div class="letter-body" style="white-space:pre-wrap;font-family:'Times New Roman',Times,serif;font-size:12pt;line-height:1.6;">${escaped}</div></body></html>`;
}

/** True when a generated letter entry has renderable content. */
export function letterHasContent(entry) {
  const data = unwrapLetterEntry(entry);
  if (!data) return false;
  return Boolean(coerceLetterHtml(data));
}

/** True when entry looks like a persisted/generated letter shell (letter_id + bureau). */
export function isGeneratedLetterShell(entry) {
  const data = unwrapLetterEntry(entry);
  if (!data) return false;
  const hasIdentity = Boolean(data.letter_id || data.id) && Boolean(data.bureau);
  const hasMethod = Boolean(
    data.package_method || data.method_used || data.method || data.prompt_family
  );
  return hasIdentity && hasMethod;
}

/** Collect raw package/letter arrays from all known result shapes. */
export function extractRawPackageEntries(result) {
  if (!result) return [];

  const candidates = [
    result.letters,
    result.packages,
    result.generated_packages,
    result.generatedPackages,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
}

/** Dev-facing audit log before deciding generation failed. */
export function logGenerationResultAudit(result, label = 'GENERATION RESULT') {
  const raw = extractRawPackageEntries(result);
  const normalized = raw.map((entry, idx) => normalizeLetterEntry(entry, idx));
  const withHtml = normalized.filter((entry) => Boolean(entry.html && String(entry.html).trim()));

  console.info(`[${label}]`, {
    keys: result ? Object.keys(result) : [],
    lettersLength: Array.isArray(result?.letters) ? result.letters.length : null,
    packagesLength: Array.isArray(result?.packages) ? result.packages.length : null,
    generatedPackagesLength: Array.isArray(result?.generated_packages)
      ? result.generated_packages.length
      : null,
    rawEntriesLength: raw.length,
    normalizedWithHtmlLength: withHtml.length,
    completedLetters: result?.completed_letters ?? null,
    totalLetters: result?.total_letters ?? null,
    runId: result?.run_id ?? null,
    sampleEntryKeys: raw[0] ? Object.keys(unwrapLetterEntry(raw[0]) || {}) : [],
    sampleHasLetterHtml: Boolean(raw[0]?.letter_html || unwrapLetterEntry(raw[0])?.letter_html),
    sampleHasLetterBody: Boolean(raw[0]?.letter_body || unwrapLetterEntry(raw[0])?.letter_body),
    sampleHasHtml: Boolean(raw[0]?.html || unwrapLetterEntry(raw[0])?.html),
  });
}

/** Extract letter entries that contain renderable content from a generation result payload. */
export function extractGeneratedLetters(result) {
  const raw = extractRawPackageEntries(result);
  return raw.filter(letterHasContent);
}

export function countGeneratedLetters(result) {
  return extractGeneratedLetters(result).length;
}

/** Normalize one backend letter/package entry for preview consumption. */
export function normalizeLetterEntry(letterOrPkg, idx = 0) {
  const letterData = unwrapLetterEntry(letterOrPkg) || {};
  const methodUsed =
    letterData.method_used ||
    letterOrPkg.method_used ||
    letterOrPkg.package_method ||
    'factual_inaccuracy';
  const merged = { ...letterOrPkg, ...letterData };
  const downloadPdfHtml =
    letterData.download_pdf_html || letterOrPkg.download_pdf_html || '';
  const previewHtmlRaw = letterData.preview_html || letterOrPkg.preview_html || '';
  const printableHtml = resolvePrintablePreviewHtml({
    ...merged,
    download_pdf_html: downloadPdfHtml,
    preview_html: previewHtmlRaw,
  });
  const postgridHtml = letterData.postgrid_html || letterOrPkg.postgrid_html || '';

  return {
    bureau: letterData.bureau || letterOrPkg.bureau || '',
    html: printableHtml,
    preview_html: printableHtml,
    download_pdf_html: downloadPdfHtml || printableHtml,
    postgrid_html: postgridHtml,
    letter_html: printableHtml,
    raw_letter_body:
      letterData.raw_letter_body ||
      letterOrPkg.raw_letter_body ||
      letterData.letter_body ||
      letterOrPkg.letter_body ||
      '',
    letter_body: letterData.letter_body || letterOrPkg.letter_body || '',
    method_used: methodUsed,
    generation_mode: letterData.generation_mode || letterOrPkg.generation_mode || 'prompt_runtime',
    letter_type: letterData.letter_type || letterOrPkg.letter_type || methodUsed,
    item_count: letterData.item_count || letterOrPkg.item_count || 0,
    items_count: letterData.item_count || letterOrPkg.item_count || 0,
    item_breakdown: letterData.item_breakdown || letterOrPkg.item_breakdown || null,
    disputed_items: letterData.disputed_items || letterOrPkg.disputed_items || [],
    package_method: letterData.package_method || letterOrPkg.package_method || methodUsed,
    package_name: letterData.package_name || letterOrPkg.package_name || '',
    package_subtype: letterData.package_subtype || letterOrPkg.package_subtype || '',
    package_item_type: letterData.package_item_type || letterOrPkg.package_item_type || '',
    item_types: letterData.item_types || letterOrPkg.item_types || [],
    id:
      letterData.id ||
      letterOrPkg.id ||
      letterData.letter_id ||
      letterOrPkg.letter_id ||
      `letter_${(letterOrPkg.bureau || 'unknown').toLowerCase()}_${idx}`,
    letter_id:
      letterData.letter_id ||
      letterOrPkg.letter_id ||
      letterData.id ||
      letterOrPkg.id ||
      `letter_${(letterOrPkg.bureau || 'unknown').toLowerCase()}_${idx}`,
    prompt_metadata: letterData.prompt_metadata || letterOrPkg.prompt_metadata || null,
    prompt_id: letterData.prompt_id || letterOrPkg.prompt_id || null,
    prompt_name: letterData.prompt_name || letterOrPkg.prompt_name || null,
    prompt_family: letterData.prompt_family || letterOrPkg.prompt_family || null,
    completeness_validation:
      letterData.completeness_validation || letterOrPkg.completeness_validation || null,
    quality_report: letterData.quality_report || letterOrPkg.quality_report || null,
  };
}

/** Count letters grouped by method/package_method from a raw generation result. */
export function lettersByMethodFromResult(result) {
  const raw = extractRawPackageEntries(result);
  const counts = {};
  raw.forEach((entry) => {
    const data = unwrapLetterEntry(entry) || entry;
    const method = data.method_used || data.package_method || data.method || 'unknown';
    counts[method] = (counts[method] || 0) + 1;
  });
  return counts;
}

/** Dev log when Step 5 (or upstream handlers) receive generation letters. */
export function logStep5ResultCounts(result, source, extra = {}) {
  const raw = extractRawPackageEntries(result);
  const normalized = normalizeGenerationResult(result, { logDrops: true });
  const methodsReceived = [
    ...new Set(
      raw.map((entry) => {
        const data = unwrapLetterEntry(entry) || entry;
        return data.method_used || data.package_method || data.method || 'unknown';
      })
    ),
  ];

  console.log(`[STEP5 RESULT SOURCE] source=${source}`);
  console.log('[STEP5 RESULT COUNTS]', {
    run_id: result?.run_id || extra.run_id || null,
    totalLettersReceived: raw.length,
    totalLettersAfterNormalize: normalized.letters.length,
    methodsReceived,
    lettersByMethod: lettersByMethodFromResult(result),
    generation_debug: result?.generation_debug || null,
    ...extra,
  });
}

/** Normalize generation result to a stable letters/packages contract for the wizard. */
export function normalizeGenerationResult(result, { logDrops = false } = {}) {
  if (!result || typeof result !== 'object') {
    return { letters: [], packages: [], letter_count: 0 };
  }

  const raw = extractRawPackageEntries(result);
  const dropped = [];
  const letters = raw
    .map((entry, idx) => normalizeLetterEntry(entry, idx))
    .filter((entry) => {
      const hasHtml = Boolean(entry.html && String(entry.html).trim());
      if (!hasHtml) {
        dropped.push({
          bureau: entry.bureau,
          method: entry.method_used || entry.package_method,
          letter_id: entry.letter_id || entry.id,
          reason: 'missing_printable_html',
        });
      }
      return hasHtml;
    });

  if (logDrops && dropped.length > 0) {
    console.warn('[NORMALIZE GENERATION RESULT] filtered letters', {
      droppedCount: dropped.length,
      keptCount: letters.length,
      dropped,
    });
  }

  return {
    ...result,
    letters,
    packages: letters,
    letter_count: letters.length,
  };
}

/** Build a letterPackage-shaped object from a raw generation result. */
export function buildLetterPackageFromResult(preGenResult, extras = {}) {
  const normalized = normalizeGenerationResult(preGenResult);
  return {
    letters: normalized.letters,
    consumer_info: preGenResult.consumer_info || {},
    bureaus_targeted: preGenResult.bureaus_targeted || [],
    generation_mode: preGenResult.generation_mode || 'prompt_runtime',
    generation_metadata: {
      method_used: preGenResult.method_used,
      strategy_used: preGenResult.strategy_used,
      generation_mode: preGenResult.generation_mode,
    },
    package_info: preGenResult.package_info || {},
    validation_result: preGenResult.validation_result || null,
    pi_validation: preGenResult.pi_validation || null,
    generated_at: new Date().toISOString(),
    ...extras,
  };
}

export function isFinalPackageReady(result) {
  if (!result) return false;
  if (result.error_code === 'LETTER_VALIDATION_FAILED') return false;
  if (result.final_package_ready === false && result.is_complete === false) return false;

  // Prefer authoritative backend package reconciliation.
  if (typeof result.is_complete === 'boolean') {
    const missing = Array.isArray(result.missing_package_ids) ? result.missing_package_ids : [];
    const duplicates = Array.isArray(result.duplicate_package_ids)
      ? result.duplicate_package_ids
      : [];
    if (result.is_complete === true && missing.length === 0 && duplicates.length === 0) {
      return true;
    }
    if (result.is_complete === false) {
      return false;
    }
  }

  if (result.final_package_ready === false) return false;

  const hasIdentity = Boolean(result.package_id || result.run_id);
  const letters = extractGeneratedLetters(result);
  const normalized = normalizeGenerationResult(result);

  // Never compare against a stale preview totalLetters value. Completeness is
  // based on presence of generated package/letter content + backend flags.
  const letterCount = Math.max(letters.length, normalized.letters.length);

  if (result.final_package_ready === true) {
    return hasIdentity && letterCount > 0 && generationHasLetters(result);
  }

  const status = String(result.status || '').toLowerCase();
  const completed =
    status === 'completed' ||
    status === 'completed_with_errors' ||
    result.success === true;

  return completed && hasIdentity && letterCount > 0 && generationHasLetters(result);
}

/**
 * Build a user-facing failure reason from backend package reconciliation.
 * Returns null when reconciliation succeeded.
 */
export function packageReconciliationFailureMessage(result) {
  if (!result) return 'Letters were not generated yet. Please retry generation.';
  if (result.is_complete === true) return null;

  const missing = Array.isArray(result.missing_package_ids) ? result.missing_package_ids : [];
  const duplicates = Array.isArray(result.duplicate_package_ids)
    ? result.duplicate_package_ids
    : [];

  if (missing.length > 0) {
    return `Generation incomplete. Missing packages: ${missing.join(', ')}`;
  }
  if (duplicates.length > 0) {
    return `Generation incomplete. Duplicate packages: ${duplicates.join(', ')}`;
  }
  if (result.final_package_ready === false) {
    return 'Generation finished but final package is not ready. Please retry.';
  }
  return 'Letters were not generated yet. Please retry generation.';
}

export function generationHasLetters(result) {
  if (!result) return false;
  if (countGeneratedLetters(result) > 0) return true;

  const normalized = normalizeGenerationResult(result);
  if (normalized.letters.length > 0) return true;

  const completed = Number(result.completed_letters || 0);
  const raw = extractRawPackageEntries(result);
  if (completed > 0 && raw.length > 0) return true;

  return raw.some(isGeneratedLetterShell);
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll generation result / round letters until HTML letters exist or timeout.
 */
export async function pollUntilLettersAvailable({
  apiUrl,
  sessionId,
  useDurable = true,
  roundId = null,
  runId = null,
  forceRegenerate = false,
  getAuthHeaders = () => ({}),
  maxAttempts = 30,
  intervalMs = 2000,
}) {
  const resultEndpoint = useDurable
    ? `${apiUrl}/api/letter-preview/durable-result/${sessionId}`
    : `${apiUrl}/api/letter-preview/generation-result/${sessionId}`;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const response = await fetch(resultEndpoint);
      if (response.ok) {
        const result = await response.json();
        const normalized = normalizeGenerationResult(result);
        if (generationHasLetters(normalized)) {
          return normalized;
        }
        if (isFinalPackageReady(normalized)) {
          return normalized;
        }
      }
    } catch {
      // keep polling
    }

    if (runId) {
      try {
        const completedRes = await fetch(`${apiUrl}/api/letter-preview/completed-letters/${runId}`);
        if (completedRes.ok) {
          const completedData = await completedRes.json();
          const completedLetters = completedData.letters || [];
          if (completedLetters.length > 0) {
            return normalizeGenerationResult({
              letters: completedLetters,
              packages: completedLetters,
              consumer_info: {},
              run_id: runId,
              completed_letters: completedLetters.length,
            });
          }
        }
      } catch {
        // keep polling
      }
    }

    // Never reuse stale round letters when the user requested a fresh generation.
    if (roundId && !runId && !forceRegenerate) {
      try {
        const headers = getAuthHeaders();
        const roundRes = await fetch(`${apiUrl}/api/disputes/${roundId}/letters`, {
          headers,
          credentials: 'include',
        });
        if (roundRes.ok) {
          const roundData = await roundRes.json();
          const roundLetters = (roundData.letters || roundData.generated_letters || []).filter(
            letterHasContent
          );
          if (roundLetters.length > 0) {
            console.warn('[POLL LETTERS] using round letters fallback (no run_id)', {
              roundId,
              letterCount: roundLetters.length,
            });
            return normalizeGenerationResult({
              letters: roundLetters,
              packages: roundLetters,
              consumer_info: roundData.consumer_info || {},
              round_id: roundId,
              _result_source: 'round_letters_fallback',
            });
          }
        }
      } catch {
        // keep polling
      }
    } else if (roundId && (runId || forceRegenerate)) {
      console.log('[POLL LETTERS] skipped stale round letters fallback', {
        runId,
        forceRegenerate,
      });
    }

    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  return null;
}

/**
 * Valid combo supersession is non-fatal (continue generation / no missing-package error).
 * Flags may live top-level on the SSE event or nested under plan_reduced.
 */
export function isNonFatalPlanReduced(data = {}) {
  const planReducedRaw = data?.plan_reduced;
  if (!planReducedRaw) return false;
  const planReducedObj =
    planReducedRaw && typeof planReducedRaw === 'object' ? planReducedRaw : null;
  const continueRun =
    data.continue_run !== undefined && data.continue_run !== null
      ? data.continue_run
      : planReducedObj?.continue_run;
  const auditOnly =
    data.audit_only !== undefined && data.audit_only !== null
      ? data.audit_only
      : planReducedObj?.audit_only;
  const reason = data.reason || planReducedObj?.reason || '';
  return (
    auditOnly === true ||
    continueRun === true ||
    String(reason).startsWith('superseded_by_combo:')
  );
}

/**
 * Only fatal error terminals should hard-fail the SSE client.
 * Incomplete terminals use failure_message / missing_packages instead of data.error.
 */
export function isFatalSseError(data = {}) {
  if (!data?.error) return false;
  if (data.terminal_kind && data.terminal_kind !== 'error') return false;
  if (data.stream_terminal && data.stage === 'incomplete') return false;
  if (data.terminal_kind === 'error') return true;
  // Legacy payloads without terminal_kind: treat bare error as fatal only when
  // there is no inline result to process first.
  return !data.result && !data.has_result;
}

/**
 * Prefer delivering an inline terminal result before treating an SSE event as a hard error.
 */
export function shouldProcessInlineTerminalResult(data = {}) {
  return Boolean(data?.result || data?.has_result);
}
